/**
 * @file useAttachmentSaveRetentionDays.ts
 * @description How many days the organization keeps a desktop-sync save before
 * removing it, or null when no such policy is configured. Consumer:
 * `AttachmentList`, for the sentence at the foot of a version strip.
 *
 * Reads the leaf and returns the number, never `snap.data` — returning the
 * parameters document would re-render every attachments panel on any identity
 * move of any organization setting. The policy is opt-in and
 * `pruneAttachmentSaves` treats a non-finite or non-positive `maxAgeDays` as no
 * policy at all, so this hook collapses those to null rather than passing a
 * number the backend would ignore. Connecting is refcounted, so sharing
 * `orgParametersStore` with `useOrganizationParameters` costs no extra listener.
 */

import { useEffect } from 'react';
import { useSnapshot } from 'valtio';

import { orgParametersStore } from '../useOrganizationParameters';

export const useAttachmentSaveRetentionDays = (): number | null => {
    useEffect(() => {
        orgParametersStore.connect();
        return () => orgParametersStore.disconnect();
    }, []);

    const snap = useSnapshot(orgParametersStore.state);
    const maxAgeDays = snap.data?.attachmentSaveRetention?.maxAgeDays;

    if (typeof maxAgeDays !== 'number' || !Number.isFinite(maxAgeDays) || maxAgeDays <= 0) {
        return null;
    }
    return maxAgeDays;
};
